import { SuppliesInventoryConfigStatusSchema } from "@redotech/redo-fulfillment-common/supplies";
import { suppliesInventoryConfigIdFormat } from "@redotech/redo-marketplace-id";
import { SuppliesTrackableProductTypeSchema } from "@redotech/redo-supplies-common/trackable-product";
import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { SortDirection } from "@redotech/redo-views-common/table";
import { unaryRpc } from "@redotech/rpc/definition";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** Null date with a coverageEnd means "after that day"; both null means no forecast. */
const ForecastedOrderDateSchema = z.object({
  date: zExt.plainDate().nullable(),
  coverageEnd: zExt.plainDate().nullable(),
  /** Set alongside `date`, so the UI can show why that day was chosen. */
  inputs: z
    .object({
      positionOnDate: z.number(),
      projectedUnits: z.number(),
      safetyStock: z.number(),
      leadTimeDays: z.number(),
    })
    .nullable(),
});

/**
 * The merchant-facing row plus the owning merchant, since this view spans every
 * account. Deliberately duplicated from the merchant SDK rather than shared —
 * the two surfaces are free to diverge.
 */
const AdminSuppliesInventoryConfigRowSchema_ = z.object({
  id: suppliesInventoryConfigIdFormat.schema,
  // Mongo Team name of the account that owns the config. Falls back to the
  // account id when no team resolves, so a row is never dropped from the table.
  merchantName: z.string(),
  // Mongo Team _id, for linking out to the merchant.
  merchantTeamId: z.string().nullable(),
  productTitle: z.string(),
  productSku: z.string().nullable(),
  productImageUrl: z.string().nullable(),
  status: SuppliesInventoryConfigStatusSchema,
  // Superseded by safetyStock. Still returned so clients built against the
  // old required schema keep validating; nullish so it can be dropped later.
  inventoryReorderThreshold: z.number().nullish(),
  safetyStock: z.number(),
  suppliesReorderQuantity: z.number(),
  unitsPerCase: z.number().nullable(),
  locationName: z.string(),
  availableInventory: z.number(),
  // Units on order that have not been received yet, in units not cases.
  incomingInventory: z.number(),
  leadTimeDays: z.number().nullable(),
  leadTimeDaysOverride: z.number().nullable(),
  trackableType: SuppliesTrackableProductTypeSchema.nullable(),
  lastOrderDate: z.date().nullable(),
  // Projected next reorder per strategy. Null date with a coverageEnd means
  // "after that day"; both null means there is no usable forecast.
  forecastedOrderTrailing: ForecastedOrderDateSchema,
  forecastedOrderFgroup: ForecastedOrderDateSchema,
});

export const AdminSuppliesInventoryConfigRowSchema: z.ZodType<AdminSuppliesInventoryConfigRow> =
  AdminSuppliesInventoryConfigRowSchema_;
export interface AdminSuppliesInventoryConfigRow extends z.infer<
  typeof AdminSuppliesInventoryConfigRowSchema_
> {}

export const getSuppliesInventoryConfigs = unaryRpc({
  name: "getSuppliesInventoryConfigs" as const,
  input: z.object({
    pageNumber: z.number(),
    pageSize: z.number(),
    sort: z
      .object({ key: z.string(), direction: createEnum(SortDirection) })
      .nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({ configs: z.array(AdminSuppliesInventoryConfigRowSchema) }),
});
